import { BaseViewStore } from 'core/stores';
import { ReceivingEditForm } from 'modules/receiving/forms';
import { action, observable, makeObservable } from 'mobx';
import { ReceivingStore } from 'modules/receiving/stores';
import { ApplicationStore } from 'platform/application/stores';
import { ProductStore } from 'modules/product/stores';
import { notificationError, isSeller, isSuperAdminOrganization, formatDate } from 'core/utils';
import { LookupStore } from 'modules/lookup/stores';
import { lookupRouteName, receivingStatus, receivingType, transferStatuses, sourceOfChange } from 'common/constants';
import moment from 'moment';
import _ from 'lodash';
import { BaasicDropdownViewStore } from 'common/stores';
import { localStorageHandler } from 'core/services';

class ReceivingEditViewStore extends BaseViewStore {
	@observable showWarningMessage = false;
	@observable warehouseOptions = [];
	receivingItem = null;
	receivingStatusOptions = [];
	receivingTypesOptions = [];
	initialWarehouseOptions = [];
	@observable productOptions = [];
	initialIsReceived = [];
	selectedProducts = [];
	@observable selectedProductsToExclude = [];
	isTransfer = false;
	isSeller = false;
	isReceivedStatus = false;
	@observable isInTransitStatus = false;
	@observable isIncomingStatus = false;
	@observable isPendingPickupStatus = false;
	isArrivedAtPortStatus = false;
	isEdit = false;
	previousStatus = null;
	dateFormat = null;
	debouncedWarehouseSearch = null;
	debouncedProductsSearch = null;
	throttleOnMenuClose = null;
	actualReceivingDateWithTime = null;
	@observable pageWarehouse = 1;
	@observable tempProductsOptions = [];
	@observable pageProducts = 1;
	@observable infiniteSearchPhrase = null;
	@observable isWarehouseDropdownLoading = false;
	@observable isProductsDropdownLoading = false;
	@observable selectedWarehouseTo = null;
	@observable selectedWarehouseFrom = null;

	form = new ReceivingEditForm({
		onSuccess: async () => {
			await this.createOrUpdateReceiving();
		},
	});

	constructor(rootStore) {
		super(rootStore);
		makeObservable(this);
		this.parentView = rootStore.productListViewStore;
		this.applicationStore = new ApplicationStore(rootStore);
		this.productStore = new ProductStore(rootStore);
		this.receivingStore = new ReceivingStore(rootStore);
		this.lookupStore = new LookupStore(rootStore);
		this.isEdit = this.rootStore.routerStore.routerState.params.id != undefined;
		this.isSeller = isSeller(rootStore);
		this.isSuperAdmin = isSuperAdminOrganization(rootStore);
		this.initialize();
	}

	@action.bound
	async initialize() {
		this.suspend();
		this.form.state.options.set({
			validateOnBlur: false,
			showErrorsOnReset: false,
			validateOnChange: true,
		});
		this.form.clear();
		this.getLocaleData();

		if (this.isSuperAdmin) {
			this.sellerDropdownStore = new BaasicDropdownViewStore({
				field: this.form.$('seller'),
				store: new ApplicationStore(this.rootStore),
				itemTemplate: {
					value: 'id',
					label: 'applicationTitle',
				},
				filterProperties: {
					sellerOnly: true,
				},
				onChangeCallback: this.clearDataOnChange,
			});
			this.form.$('seller').set('rules', 'required');
		} else {
			await this.getProductOptions();
			await this.getWarehouseOptions();
		}

		this.debouncedWarehouseSearch = _.debounce((searchPhrase, { action }) => {
			this.searchWarehouseAsync(searchPhrase, { action });
		}, 500);
		this.debouncedProductsSearch = _.debounce((searchPhrase, { action }) => {
			this.searchProductsAsync(searchPhrase, { action });
		}, 500);
		this.throttleOnMenuClose = _.throttle(() => this.onMenuCloseProduct(), 500);

		await this.getReceivingStatusOptions();
		await this.getReceivingTypeOptions();
		if (this.isEdit) {
			await this.findReceiving();
			this.setInitialIsReceived(this.receivingStore.item);
			if (this.receivingItem.receivingStatus.extra.abrv == receivingStatus.arrivedAtPort) {
				_.remove(this.receivingStatusOptions, item => {
					return item.extra.abrv == receivingStatus.incoming;
				});
			}
		} else {
			this.form.clear();
			this.form
				.$('products')
				.add([{ id: '', quantity: '', selectedProduct: null, description: '', isReceived: false }]);
			this.form.$('receivingStatus').set(
				'value',
				_.find(this.receivingStatusOptions, item => {
					if (item.label.toLowerCase() == receivingStatus.incoming) {
						return { value: item.id, label: item.name };
					}
				})
			);
			this.form.$('receivingType').set(
				'value',
				_.find(this.receivingTypeOptions, item => {
					if (item.label.toLowerCase() == receivingType.container) {
						return { value: item.id, label: item.name };
					}
				})
			);
			this.isTransfer = false;
			this.form.$('warehouseTo').set('rules', 'required');
			if (this.isSuperAdmin) {
				const seller = localStorageHandler.get('selectedSeller');
				if (seller) {
					this.sellerDropdownStore.setValue(seller);
					await this.sellerDropdownStore.reload();
				}
			}
		}
		this.previousStatus = this.form.$('receivingStatus').value;
		this.resume();
	}

	findReceiving = async () => {
		await this.getReceiving(this.rootStore.routerStore.routerState.params.id, {
			embed: [
				'receivingStatus',
				'receivingType',
				'receivingProducts',
				'receivingProducts.product',
				'warehouseTo',
				'warehouseFrom',
			],
		});
	};

	@action.bound
	clearDataOnChange = async sellerDropdownValue => {
		await Promise.all([this.clearProductData(sellerDropdownValue), this.clearWarehouseData(sellerDropdownValue)]);
	};

	@action.bound
	clearProductData = async sellerDropdownValue => {
		if (this.isEdit) {
			this.sellerId = this.receivingItem.sellerId;
		} else {
			this.sellerId = sellerDropdownValue.value;
			this.productOptions = [];
			this.selectedProducts = [];
			this.selectedProductsToExclude = [];
			this.clearSelectedProductFields();
		}
		await this.getProductOptions();
	};

	@action.bound
	clearWarehouseData = async sellerDropdownValue => {
		if (this.isEdit) {
			this.sellerId = this.receivingItem.sellerId;
		} else {
			this.sellerId = sellerDropdownValue.value;
			this.warehouseOptions = [];
			this.selectedWarehouseFrom = null;
			this.selectedWarehouseTo = null;
			this.clearSelectedWarehouseFields();
		}
		await this.getWarehouseOptions();
	};

	@action.bound
	getLocaleData() {
		let locale = window.navigator.userLanguage || window.navigator.language;
		moment.locale(locale);
		let localeData = moment.localeData();
		this.dateFormat = localeData.longDateFormat('L');
	}

	@action.bound
	setInitialIsReceived(receiving) {
		this.initialIsReceived = _.map(receiving.receivingProducts, item => {
			return { id: item.id, isReceived: item.isReceived };
		});
		_.forEach(this.form.$('products').value, item => {
			this.selectedProductsToExclude.push(item.selectedProduct.value);
		});
	}

	@action.bound
	isReceivingProductCheckboxDisabled = id => {
		const initialIsReceived = _.find(this.initialIsReceived, item => {
			return item.id == id;
		});
		if (initialIsReceived != null) {
			return initialIsReceived.isReceived;
		} else {
			return false;
		}
	};

	@action.bound
	async getReceivingStatusOptions() {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.receivingStatus);
		this.receivingStatusOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
		if (!this.isSeller && !this.isSuperAdmin) {
			_.remove(this.receivingStatusOptions, item => {
				return item.label === 'Received';
			});
		}
	}

	@action.bound
	async getReceivingTypeOptions() {
		await this.lookupStore.find({ page: 1, rpp: 50 }, lookupRouteName.receivingType);
		this.receivingTypeOptions = _.map(this.lookupStore.data, item => {
			return { value: item.id, label: item.name, extra: item };
		});
	}

	@action.bound
	getReceiving = async (id, options) => {
		await this.receivingStore.getReceiving(id, options);
		const item = this.receivingStore.item;
		this.receivingItem = this.receivingStore.item;

		item.estimatedReceivingDate = moment(item.estimatedReceivingDate)
			.tz(Intl.DateTimeFormat().resolvedOptions().timeZone)
			.format(this.dateFormat);
		if (item.actualReceivingDate != null) {
			// save date with time
			this.actualReceivingDateWithTime = item.actualReceivingDate;
			item.actualReceivingDate = formatDate(item.actualReceivingDate, true, this.dateFormat); // to get proper date inside date picker
		}

		if (item.warehouseFrom != null) {
			item.warehouseFrom = {
				value: item.warehouseFrom.id,
				label: item.warehouseFrom.applicationTitle,
				extra: {
					...item.warehouseFrom,
				},
			};
			this.isTransfer = true;
			this.form.$('warehouseFrom').set('rules', 'required');
			this.form.$('cost').set('rules', 'required|numeric');
		} else if (item.warehouseFromName != '') {
			item.warehouseFrom = {
				label: item.warehouseFromName,
			};

			this.isTransfer = true;
		}

		if (item.warehouseTo != null) {
			item.warehouseTo = {
				value: item.warehouseTo.id,
				label: item.warehouseTo.applicationTitle,
				extra: {
					...item.warehouseTo,
				},
			};
		} else if (item.warehouseToName != '') {
			item.warehouseTo = {
				label: item.warehouseToName,
			};
		}

		item.receivingStatus = {
			value: item.receivingStatus.id,
			label: item.receivingStatus.name,
			extra: {
				...item.receivingStatus,
			},
		};

		item.receivingType = {
			value: item.receivingType.id,
			label: item.receivingType.name,
			extra: {
				...item.receivingType,
			},
		};
		this.form.update(item);

		if (this.isSuperAdmin) {
			this.sellerDropdownStore.setValue({ value: this.receivingItem.sellerId });
			await this.sellerDropdownStore.reload();
		}

		this.previousStatus = this.form.$('receivingStatus').value;
		// Sets the currently selected receiving status.
		switch (item.receivingStatus.extra.abrv) {
			case receivingStatus.inTransit:
				this.isInTransitStatus = true;
				this.isArrivedAtPortStatus = false;
				this.isPendingPickupStatus = false;
				this.isIncomingStatus = false;
				break;
			case receivingStatus.pendingPickup:
				this.isPendingPickupStatus = true;
				this.isArrivedAtPortStatus = false;
				this.isInTransitStatus = false;
				this.isIncomingStatus = false;
				this.isReceivedStatus = false;
				break;
			case receivingStatus.incoming:
				this.isIncomingStatus = true;
				this.isPendingPickupStatus = false;
				this.isArrivedAtPortStatus = false;
				this.isInTransitStatus = false;
				this.isReceivedStatus = false;
				break;
			case receivingStatus.arrivedAtPort:
				this.isArrivedAtPortStatus = true;
				this.isIncomingStatus = false;
				this.isPendingPickupStatus = false;
				this.isInTransitStatus = false;
				this.isReceivedStatus = false;
				break;
			case receivingStatus.received:
				this.isReceivedStatus = true;
				this.isArrivedAtPortStatus = false;
				this.isIncomingStatus = false;
				this.isPendingPickupStatus = false;
				this.isInTransitStatus = false;
				break;
		}
		if (item.warehouseFromName != '' || item.warehouseFrom != null) {
			this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
				return transferStatuses.indexOf(item.extra.abrv) > -1 || item.extra.abrv === receivingStatus.received;
			});
			if (this.isInTransitStatus) {
				this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
					return item.extra.abrv != receivingStatus.pendingPickup;
				});
			}
		} else {
			this.receivingStatusOptions = _.filter(this.receivingStatusOptions, item => {
				return !(transferStatuses.indexOf(item.extra.abrv) > -1) || item.extra.abrv === receivingStatus.received;
			});
		}
		if (this.receivingItem.receivingStatus.extra.abrv === receivingStatus.pendingPickup) {
			_.remove(this.receivingStatusOptions, item => {
				return item.label === 'Received';
			});
		}
		_.each(item.receivingProducts, item => {
			const selectedProduct = {
				value: item.productId,
				label: item.product.mainSku,
			};
			this.form.$('products').add([
				{
					id: item.id,
					quantity: item.quantity,
					description: item.description,
					selectedProduct: selectedProduct,
					isReceived: item.isReceived,
				},
			]);
			this.selectedProducts.push(selectedProduct);
			const itemIdx = _.findIndex(this.productOptions, item => {
				return item.value == selectedProduct.value;
			});
			this.productOptions.splice(itemIdx, 1);
		});
	};

	@action.bound
	async getWarehouseOptions() {
		await this.applicationStore.find({
			rpp: 15,
			page: 1,
			warehouseOnly: true,
			isOrganizationActive: true,
			includeOnlySellerWarehouses: !this.isEdit,
			sellerWarehousesOnlyOrganizationId: this.sellerId,
		});
		this.warehouseOptions = _.map(this.applicationStore.data, item => {
			return { value: item.id, label: item.applicationTitle };
		});
		if (this.isTransfer === true) {
			if (this.form.$('warehouseTo').value) {
				this.warehouseOptions = this.warehouseOptions.filter(item => {
					return item.value !== this.form.$('warehouseTo').value.value;
				});
			}
			if (this.form.$('warehouseFrom').value) {
				this.warehouseOptions = this.warehouseOptions.filter(item => {
					return item.value !== this.form.$('warehouseFrom').value.value;
				});
			}
		}
		this.initialWarehouseOptions = this.warehouseOptions;
	}

	@action.bound
	async searchWarehouseAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.page = 1;
		}
		if (action === 'input-change') {
			this.isWarehouseDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageWarehouse = 1;
			this.warehouseOptions = [];
			await this.applicationStore.find({
				search: searchPhrase || null,
				page: 1,
				rpp: 15,
				warehouseOnly: true,
				isOrganizationActive: true,
				includeOnlySellerWarehouses: !this.isEdit,
				sellerWarehousesOnlyOrganizationId: this.sellerId,
			});
			const newOptions = [];
			_.forEach(this.applicationStore.data, item => {
				newOptions.push({ value: item.id, label: item.applicationTitle, extra: item });
			});
			this.warehouseOptions = newOptions;
			this.isWarehouseDropdownLoading = false;
		}
	}

	@action.bound
	async searchProductsAsync(searchPhrase, { action }) {
		if (searchPhrase === '') {
			this.pageProducts = 1;
		}
		if (action === 'input-change') {
			this.isProductsDropdownLoading = true;
			this.infiniteSearchPhrase = searchPhrase;
			this.pageProducts = 1;
			this.productOptions = [];
			await this.productStore.findProduct({
				searchFields: ['mainSku'],
				search: searchPhrase || null,
				rpp: 15,
				page: 1,
				productIdsToExclude: this.selectedProductsToExclude,
				embed: ['productInventories'],
				sellerIds: this.sellerId ? [this.sellerId] : null,
			});
			const newOptions = [];
			_.forEach(this.productStore.data, product => {
				if (!product.isMultiProduct) {
					newOptions.push({ label: product.mainSku, value: product.id });
				}
			});
			this.productOptions = newOptions;
			if (searchPhrase === '') {
				await this.getProductOptions();
			} else {
				this.productOptions = newOptions;
			}
			this.isProductsDropdownLoading = false;
		}
	}

	@action.bound
	async handleWarehouseOnScroll() {
		if (this.warehouseOptions.length < this.pageWarehouse * 13) return;
		this.isWarehouseDropdownLoading = true;
		this.pageWarehouse++;
		await this.applicationStore.find({
			search: this.infiniteSearchPhrase,
			page: this.pageWarehouse,
			rpp: 15,
			warehouseOnly: true,
			isOrganizationActive: true,
			includeOnlySellerWarehouses: !this.isEdit,
			sellerWarehousesOnlyOrganizationId: this.sellerId,
		});
		if (this.applicationStore.response.item.length > 0) {
			const newOptions = _.map(this.applicationStore.data, item => {
				return { value: item.id, label: item.applicationTitle };
			});
			this.warehouseOptions = this.warehouseOptions.concat(newOptions);
		}
		this.isWarehouseDropdownLoading = false;
	}

	@action.bound
	async onMenuCloseWarehouse() {
		this.pageWarehouse = 1;
		this.infiniteSearchPhrase = null;
		await this.getWarehouseOptions();
	}

	@action.bound
	async onMenuCloseProduct() {
		this.pageProducts = 1;
		this.productOptions = [];
		this.infiniteSearchPhrase = null;
		await this.getProductOptions();
	}

	@action.bound
	async handleProductsOnScroll() {
		if (this.productOptions.length < this.pageProducts * 15) return;
		this.isProductsDropdownLoading = true;
		this.pageProducts++;
		await this.productStore.findProduct({
			search: this.infiniteSearchPhrase || null,
			rpp: 15,
			page: this.pageProducts,
			productIdsToExclude: this.selectedProductsToExclude,
			embed: ['productInventories'],
			searchFields: ['mainSku'],
			sellerIds: this.sellerId ? [this.sellerId] : null,
		});
		_.forEach(this.productStore.data, product => {
			if (!product.isMultiProduct) {
				this.tempProductsOptions.push({ label: product.mainSku, value: product.id });
			}
		});
		if (this.tempProductsOptions.length > 0) {
			this.productOptions = this.productOptions.concat(this.tempProductsOptions);
			this.tempProductsOptions = [];
		}
		this.isProductsDropdownLoading = false;
	}

	@action.bound
	async getProductOptions() {
		await this.productStore.findProduct({
			rpp: 15,
			page: 1,
			productIdsToExclude: this.selectedProductsToExclude,
			sellerIds: this.sellerId ? [this.sellerId] : null,
		});
		const newOptions = [];
		_.forEach(this.productStore.data, product => {
			if (!product.isMultiProduct) {
				newOptions.push({ label: product.mainSku, value: product.id });
			}
		});
		this.productOptions = newOptions;
	}

	@action.bound
	updateActualReceivingDate(isReceived) {
		//get utc datetime of right now
		this.actualReceivingDateWithTime = isReceived ? moment.utc(moment(new Date())).format() : null;
		this.form
			.$('actualReceivingDate')
			.set('value', formatDate(this.actualReceivingDateWithTime, true, this.dateFormat));
	}

	@action.bound
	async createOrUpdateReceiving() {
		this.suspend();
		try {
			const {
				warehouseTo,
				warehouseFrom,
				ref,
				products,
				estimatedReceivingDate,
				receivingStatus,
				receivingType,
				cost,
			} = this.form.values();
			const receivingProducts = [];
			_.forEach(products, item => {
				const receivingProduct = {
					id: item.id,
					productId: item.selectedProduct.value,
					product: { id: item.selectedProduct.value, mainSku: item.selectedProduct.label },
					quantity: item.quantity,
					description: item.description,
					isReceived: item.isReceived,
				};
				receivingProducts.push(receivingProduct);
			});
			let receiving = {
				ref: ref,
				receivingProducts: receivingProducts,
				warehouseToId: warehouseTo.value,
				warehouseFromId: warehouseFrom ? warehouseFrom.value : null,
				estimatedReceivingDate: estimatedReceivingDate,
				receivingStatusId: receivingStatus.value,
				receivingTypeId: receivingType.value,
				cost: cost,
				actualReceivingDate: this.actualReceivingDateWithTime,
			};
			if (this.isEdit) {
				receiving.id = this.rootStore.routerStore.routerState.params.id;
				receiving.sellerId = this.receivingItem.sellerId;
				receiving.warehouseFromName = this.receivingItem.warehouseFromName;
				receiving.warehouseToName = this.receivingItem.warehouseToName;

				await this.receivingStore.updateReceiving(receiving, sourceOfChange.updateReceiving);
				this.rootStore.notificationStore.success([
					'CONTAINERFORM.NOTIFICATIONS.UPDATE',
					{ name: this.form.$('ref').value },
				]);
			} else {
				receiving.sellerId = this.isSuperAdmin ? this.sellerId : null;
				await this.receivingStore.createReceiving(receiving, sourceOfChange.createReceiving);
				this.rootStore.notificationStore.success(['CONTAINERFORM.NOTIFICATIONS.CREATE', { name: ref }]);
			}
			this.rootStore.routerStore.goTo('master.app.main.receiving.list');
		} catch (err) {
			this.resume();
			let errorMessage;
			if (
				_.get(err, 'data') === 'CONTAINERFORM.NOTIFICATIONS.MISSINGINVENTORY' ||
				_.get(err, 'data') === 'CONTAINERFORM.NOTIFICATIONS.WMSSHIPPED'
			) {
				errorMessage = _.get(err, 'data');
			} else if (_.get(err, 'data.message') === 'CONTAINERFORM.NOTIFICATIONS.INVALIDSTATUS') {
				errorMessage = _.get(err, 'data.message');
			} else {
				errorMessage = undefined;
			}
			if (errorMessage != undefined) {
				if (errorMessage === 'CONTAINERFORM.NOTIFICATIONS.WMSSHIPPED') {
					this.selectedProducts = [];
					const length = this.form.$('products').value.length;
					for (var i = 0; i < length; i++) {
						this.form.$('products').del(i);
					}
					await this.findReceiving();
				}
				this.rootStore.notificationStore.error(errorMessage);
			} else {
				notificationError({ store: this.rootStore.notificationStore, err });
			}
		}
	}

	@action.bound
	updateReceivingOptions(value, field) {
		if (field.value == '' || field.value == undefined) {
			this.selectedProducts.push(value);
		} else {
			if (value != null) {
				const selectedProduct = field.value;
				this.selectedProducts.splice(
					_.findIndex(this.selectedProducts, item => {
						return (item.value = selectedProduct.value);
					}),
					1
				);
				this.selectedProducts.push(value);
				this.productOptions.push(selectedProduct);
			}
		}
		this.productOptions = _.difference(this.productOptions, this.selectedProducts);
	}

	@action.bound
	onWarehouseChange = (value, warehouseToField, warehouseFromField, isTransfer, isWarehouseTo) => {
		if (value != null) {
			this.warehouseOptions = _.remove(this.warehouseOptions, item => {
				return !(item.value === value.value);
			});
			if (isWarehouseTo) {
				this.selectedWarehouseTo = value;
				this.warehouseOptions.push(warehouseToField.value);
				this.form.$(warehouseToField.path).value = value;
			} else {
				this.selectedWarehouseFrom = value;
				this.warehouseOptions.push(warehouseFromField.value);
				this.form.$(warehouseFromField.path).value = value;
			}
		} else {
			if (isTransfer) {
				this.selectedWarehouseFrom = null;
				this.warehouseOptions.push(warehouseFromField.value);
				this.form.$(warehouseFromField.path).value = '';
			} else {
				this.selectedWarehouseTo = null;
				this.warehouseOptions.push(warehouseToField.value);
				this.form.$(warehouseToField.path).value = '';
			}
		}

		if (isTransfer) {
			if (value != null) {
				this.setReceivingStatus(receivingStatus.pendingPickup);
			} else {
				this.setReceivingStatus(receivingStatus.incoming);
			}
		}
	};

	@action.bound
	setReceivingStatus(status) {
		if (this.form.$('receivingStatus').value.extra.abrv != receivingStatus.received) {
			this.previousStatus = this.form.$('receivingStatus').value;
		}
		const receivingStatusItem = _.find(this.receivingStatusOptions, item => {
			return item.extra.abrv == status;
		});
		this.form.$('receivingStatus').set('value', receivingStatusItem);
	}

	@action.bound
	onReceivingStatusChange = (value, field) => {
		if (
			this.previousStatus != null &&
			this.previousStatus.value != value.value &&
			field.value.extra.abrv != receivingStatus.received
		) {
			this.previousStatus = field.value;
		}
		this.form.$(field.path).set('value', value);

		// Sets the currently selected receiving status.
		switch (value.extra.abrv) {
			case receivingStatus.inTransit:
				this.isInTransitStatus = true;
				this.isArrivedAtPortStatus = false;
				this.isPendingPickupStatus = false;
				this.isIncomingStatus = false;
				break;
			case receivingStatus.pendingPickup:
				this.isPendingPickupStatus = true;
				this.isArrivedAtPortStatus = false;
				this.isInTransitStatus = false;
				this.isIncomingStatus = false;
				break;
			case receivingStatus.incoming:
				this.isIncomingStatus = true;
				this.isPendingPickupStatus = false;
				this.isArrivedAtPortStatus = false;
				this.isInTransitStatus = false;
				break;
			case receivingStatus.arrivedAtPort:
				this.isArrivedAtPortStatus = true;
				this.isIncomingStatus = false;
				this.isPendingPickupStatus = false;
				this.isInTransitStatus = false;
				break;
		}
		if (value.label.toLowerCase() == receivingStatus.received) {
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
			this.form.$('products').each(field => {
				if (field.id.includes('isReceived')) {
					field.set('value', true);
				}
			});
		} else {
			this.showWarningMessage = false;
			this.updateActualReceivingDate(false);
			this.form.$('products').each(field => {
				if (field.id.includes('isReceived') && field.isDirty) {
					field.set('value', false);
				}
			});
		}
	};

	@action.bound
	onReceivingTypeChange = (value, field) => {
		this.form.$(field.path).set('value', value);
		if (value.extra.abrv.includes('transfer')) {
			this.isTransfer = true;
			this.getWarehouseOptions();
			this.form.$('warehouseFrom').set('rules', 'required');
			this.form.$('warehouseTo').set('rules', '');
			this.form.$('cost').set('rules', 'required|numeric');
			this.setReceivingStatus(receivingStatus.pendingPickup);
		} else {
			this.isTransfer = false;
			this.form.$('warehouseFrom').set('value', null);
			this.form.$('warehouseFrom').set('rules', null);
			this.form.$('warehouseTo').set('rules', 'required');
			this.form.$('cost').set('rules', null);
			this.setReceivingStatus(receivingStatus.incoming);
		}
	};

	@action.bound
	onProductChange = (value, field) => {
		this.updateReceivingOptions(value, field);
		if (value != null) {
			if (_.find(this.selectedProducts, p => p.value === value.value)) {
				this.selectedProductsToExclude.push(value.value);
			}
		} else {
			var index = this.selectedProductsToExclude.indexOf(this.form.$(field.path).value.value);
			if (index !== -1) {
				this.selectedProductsToExclude.splice(index, 1);
			}
		}
		this.form.$(field.path).set('value', value);
	};

	@action.bound
	clearSelectedProductFields = () => {
		this.form.$('products').each(field => {
			if (field.id.includes('selectedProduct')) {
				field.set('value', null);
			}
		});
	};

	@action.bound
	clearSelectedWarehouseFields = () => {
		this.form.$('warehouseFrom').set('value', null);
		this.form.$('warehouseTo').set('value', null);
	};

	@action.bound
	addProduct = () => {
		this.form.$('products').add([{ id: '', quantity: '', selectedProduct: null, description: '', isReceived: false }]);
		if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
			this.form.$('receivingStatus').set('value', this.previousStatus);
			this.showWarningMessage = false;
			this.updateActualReceivingDate(false);
		}
		// _.forEach(this.form.$('products').value, item => {
		// 	this.selectedProductsToExclude.push(item.selectedProduct.value);
		// });
	};

	@action.bound
	onCheck = (evt, field) => {
		this.form.$(field.path).value = evt.currentTarget.checked;
		this.checkReceivingProducts();
	};

	@action.bound
	checkReceivingProducts() {
		const receivingProduct = _.find(this.form.$('products').value, item => {
			return item.isReceived == false;
		});
		if (receivingProduct == undefined) {
			this.setReceivingStatus(receivingStatus.received);
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
		} else {
			if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
				this.form.$('receivingStatus').set('value', this.previousStatus);
			}
			if (this.previousStatus.extra.abrv != receivingStatus.received) {
				this.showWarningMessage = false;
				this.updateActualReceivingDate(false);
			} else {
				this.showWarningMessage = true;
				this.updateActualReceivingDate(true);
			}
		}
	}

	@action.bound
	onDelete = (e, field) => {
		if (field.value.selectedProduct != '' && field.value.value != null) {
			const { selectedProduct } = field.value;
			this.selectedProducts.splice(
				_.findIndex(this.selectedProducts, item => {
					return item.value === selectedProduct.value;
				}),
				1
			);
			this.productOptions.push(selectedProduct);
		} else {
			var index = this.selectedProductsToExclude.indexOf(field.value.selectedProduct.value);
			if (index !== -1) {
				this.selectedProductsToExclude.splice(index, 1);
			}
			this.getProductOptions();
		}
		if (this.form.$('receivingStatus').value.extra.abrv == receivingStatus.received) {
			const receivingProduct = _.find(this.form.$('products').value, item => {
				return item.isReceived == false;
			});
			if (receivingProduct != undefined) {
				this.form.$('receivingStatus').set('value', this.previousStatus);
			} else {
				this.setReceivingStatus(receivingStatus.received);
			}
		}
		this.form.$(field.path).onDel(e);
		const receivingProducts = this.form.$('products').value;
		const receivingProduct = _.find(receivingProducts, item => {
			return item.isReceived == false;
		});
		if (receivingProduct == undefined && receivingProducts != undefined && receivingProducts.length > 0) {
			this.setReceivingStatus(receivingStatus.received);
			this.showWarningMessage = true;
			this.updateActualReceivingDate(true);
		}
	};
}

export default ReceivingEditViewStore;



// WEBPACK FOOTER //
// ./src/modules/receiving/stores/ReceivingEditViewStore.js